/**
 * stores/messages.js — per-(phone, channel) message arrays + per-message reactions.
 *
 * S5 — composite key refactor. Pre-refactor, threads were keyed by phone
 * alone, which meant a single contact messaging on two WhatsApp channels
 * shared one $threads entry: realtime inserts on channel B appended into
 * the array the user was viewing for channel A, polluting the thread.
 * With composite keys `${phone}:${channelId}`, each per-channel chat row
 * gets its own independent message array.
 *
 * Keyed by `${phone}:${channelId}`. Each thread has its own array.
 * Optimistic sends append to the array immediately, status updates patch
 * in place, realtime inserts append at the bottom.
 *
 * Reactions are stored separately in $reactions (NOT as messages in the
 * thread) because WhatsApp models them as metadata on a target message,
 * not as standalone bubbles. See the reaction helpers below.
 */

import { map, atom, computed } from 'nanostores';
import { $openChatPhone, $openChatChannel } from './ui.js';

// Composite key helper — single source of truth for every reader/writer.
// Falls back to phone alone when channelId is missing so callers mid-boot
// (no channel resolved yet) still land in a stable bucket. That fallback
// should never fire in steady state; it's defensive for boot order races.
function _threadKey(phone, channelId) {
  if (!phone) return null;
  return channelId ? `${phone}:${channelId}` : String(phone);
}

// `${phone}:${channelId}` → message array (sorted oldest → newest).
export const $threads = map({});

// Loading state per thread (composite-keyed).
export const $threadLoading = map({});

// Derived: messages for the currently open thread.
// Components subscribing to this only re-render when the OPEN thread changes,
// not when ANY thread updates. Critical for performance with many active chats.
export const $currentMessages = computed(
  [$threads, $openChatPhone, $openChatChannel],
  (threads, phone, channelId) => {
    const key = _threadKey(phone, channelId);
    return key ? (threads[key] || []) : [];
  }
);

// ── Thread eviction ─────────────────────────────────────────────────────────
// #203: agents who open 100+ chats in a long session keep every message array
// in memory. Evict the least-recently-opened threads when the count exceeds
// MAX_CACHED. The currently open thread is never evicted.
// S5: tracks composite keys instead of bare phones.
const MAX_CACHED = 20;
const _accessOrder = []; // most recent at the end

function _touchThread(key) {
  if (!key) return;
  const idx = _accessOrder.indexOf(key);
  if (idx !== -1) _accessOrder.splice(idx, 1);
  _accessOrder.push(key);
}

function _evictStaleThreads(currentKey) {
  if (_accessOrder.length <= MAX_CACHED) return;
  const toEvict = [];
  while (_accessOrder.length > MAX_CACHED) {
    const oldest = _accessOrder.shift();
    if (oldest === currentKey) { _accessOrder.push(oldest); continue; }
    toEvict.push(oldest);
  }
  for (const key of toEvict) {
    $threads.setKey(key, undefined);
    // Purge reactions for evicted thread — reaction keys start with `${key}::`.
    const prefix = `${key}::`;
    const all = $reactions.get();
    for (const rk in all) {
      if (rk.startsWith(prefix)) $reactions.setKey(rk, undefined);
    }
  }
}

// ── Actions ──────────────────────────────────────────────────────────────────

/** Replace messages for a thread (after API load) */
export function setMessages(phone, channelId, messages) {
  const key = _threadKey(phone, channelId);
  if (!key) return;
  $threads.setKey(key, messages);
  _touchThread(key);
  _evictStaleThreads(key);
}

/**
 * Prepend older messages to a thread (load-older pagination).
 * De-dupes against the existing head by wamid — a retry or overlap window
 * in page boundaries could otherwise double-insert the same row.
 */
export function prependMessages(phone, channelId, olderMessages) {
  if (!olderMessages || olderMessages.length === 0) return;
  const key = _threadKey(phone, channelId);
  if (!key) return;
  const current = $threads.get()[key] || [];
  const existingIds = new Set();
  for (const m of current) {
    if (m.wamid) existingIds.add(m.wamid);
    if (m.id)    existingIds.add(m.id);
  }
  const deduped = olderMessages.filter(m =>
    !(m.wamid && existingIds.has(m.wamid)) && !(m.id && existingIds.has(m.id))
  );
  if (deduped.length === 0) return;
  $threads.setKey(key, [...deduped, ...current]);
}

/** Append a single message (optimistic send or realtime insert) */
export function appendMessage(phone, channelId, message) {
  const key = _threadKey(phone, channelId);
  if (!key) return;
  const current = $threads.get()[key] || [];
  // De-dupe by wamid to prevent realtime + optimistic double-render
  if (message.wamid && current.some(m => m.wamid === message.wamid)) return;
  $threads.setKey(key, [...current, message]);
}

/** Update an existing message by wamid (status, reactions, deletes) */
export function patchMessage(phone, channelId, wamid, patch) {
  const key = _threadKey(phone, channelId);
  if (!key) return;
  const current = $threads.get()[key] || [];
  const next = current.map(m =>
    (m.wamid === wamid || m.id === wamid) ? { ...m, ...patch } : m
  );
  $threads.setKey(key, next);
}

/** Replace optimistic message with real one (after server confirms wamid) */
export function reconcileOptimistic(phone, channelId, tempId, realMessage) {
  const key = _threadKey(phone, channelId);
  if (!key) return;
  const current = $threads.get()[key] || [];
  const next = current.map(m => (m.id === tempId ? { ...realMessage } : m));
  $threads.setKey(key, next);
}

/** Remove a message (e.g., failed send retry) */
export function removeMessage(phone, channelId, idOrWamid) {
  const key = _threadKey(phone, channelId);
  if (!key) return;
  const current = $threads.get()[key] || [];
  $threads.setKey(key, current.filter(m => m.id !== idOrWamid && m.wamid !== idOrWamid));
}

/** Clear all messages for a thread (e.g., on logout) */
export function clearThread(phone, channelId) {
  const key = _threadKey(phone, channelId);
  if (!key) return;
  $threads.setKey(key, []);
}

/** Get messages for a (phone, channel) pair — non-reactive. */
export function getMessages(phone, channelId) {
  const key = _threadKey(phone, channelId);
  if (!key) return [];
  return $threads.get()[key] || [];
}

// ── Reactions ────────────────────────────────────────────────────────────────
//
// Reactions live in their own map keyed by `${phone}:${channelId}::${targetWamid}`.
// Each entry is an array of reactions on that message: [{emoji, fromMe, fromPhone,
// timestamp, wamid}]. WhatsApp semantics: at most one reaction per (sender,
// message) — sending a new emoji from the same sender REPLACES the previous,
// sending an empty emoji REMOVES it.
//
// S5: composite key includes channelId so reactions on the same contact's
// messages across two channels don't cross-contaminate.
//
// MessageBubble subscribes to $reactions and looks up its own reactions by
// BOTH `wamid` and `metaWamid` (because outbound messages have wamid=queue_id
// and metaWamid=Meta wamid, and customer reactions reference the Meta wamid).

export const $reactions = map({});

function _reactionKey(phone, channelId, targetWamid) {
  const threadKey = _threadKey(phone, channelId);
  if (!threadKey || !targetWamid) return null;
  return `${threadKey}::${targetWamid}`;
}

/**
 * Add or replace a reaction on a target message. Empty emoji removes any
 * existing reaction from that sender. fromMe=true identifies the agent;
 * fromPhone identifies the customer. The "one per sender" semantics filter
 * any prior reaction from the same identity before appending.
 */
export function addReaction(phone, channelId, targetWamid, reaction) {
  const key = _reactionKey(phone, channelId, targetWamid);
  if (!key) return;
  const current = $reactions.get()[key] || [];

  // Remove any prior reaction from this sender
  const sameSender = (r) =>
    reaction.fromMe ? r.fromMe : (!r.fromMe && r.fromPhone === reaction.fromPhone);
  const filtered = current.filter(r => !sameSender(r));

  // Empty emoji = removal request — just leave the filtered list as-is
  if (!reaction.emoji) {
    if (filtered.length === current.length) return;
    $reactions.setKey(key, filtered);
    return;
  }

  // Append the new reaction
  $reactions.setKey(key, [...filtered, reaction]);
}

/** Remove a sender's reaction from a target message (e.g. send-failure rollback). */
export function removeReaction(phone, channelId, targetWamid, fromMe = true, fromPhone = null) {
  const key = _reactionKey(phone, channelId, targetWamid);
  if (!key) return;
  const current = $reactions.get()[key] || [];
  const filtered = current.filter(r =>
    fromMe ? !r.fromMe : !(!r.fromMe && r.fromPhone === fromPhone)
  );
  if (filtered.length === current.length) return;
  $reactions.setKey(key, filtered);
}

/** Non-reactive read of reactions for a target message. */
export function getReactionsFor(phone, channelId, targetWamid) {
  const key = _reactionKey(phone, channelId, targetWamid);
  if (!key) return [];
  return $reactions.get()[key] || [];
}

// ── Compat helper — build the reaction key so components can index
//    directly into the $reactions map without calling _reactionKey
//    (which is private). Used by MessageBubble for its dual wamid
//    lookup pattern.
export function reactionKey(phone, channelId, targetWamid) {
  return _reactionKey(phone, channelId, targetWamid);
}
